import React from 'react';
import { Heart, Target, Users, Award } from 'lucide-react';

const About = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)', textAlign: 'center' }}>
        <div className="container">
          <h1 className="hero-large" style={{ marginBottom: '24px' }}>About Delhi Guided Tours</h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
            Premium heritage experiences led by passionate storytellers who bring Delhi's rich history to life.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div>
              <h2 className="heading-1" style={{ marginBottom: '24px' }}>Our Story</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                  Delhi Guided Tours was founded by Joshua Massey with a clear vision: to transform how people experience Delhi's incredible heritage.
                </p>
                <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                  We believe that history isn't just about dates and facts - it's about stories, people, and the moments that shaped civilizations. Every monument has a tale to tell, and we're here to share those stories in the most engaging way possible.
                </p>
                <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                  What started as a passion project has grown into Delhi's trusted name for premium heritage tours, serving travelers from around the world who seek authentic, expert-led experiences.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <h2 className="heading-1" style={{ textAlign: 'center', marginBottom: '60px' }}>What Drives Us</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '48px' }}>
            <div>
              <div style={{ width: '60px', height: '60px', border: '1px solid var(--border-medium)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px' }}>
                <Heart size={28} color="var(--text-primary)" />
              </div>
              <h3 className="heading-3" style={{ marginBottom: '12px' }}>Passion for Heritage</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                We're not just tour operators - we're history enthusiasts who live and breathe Delhi's rich cultural tapestry.
              </p>
            </div>
            <div>
              <div style={{ width: '60px', height: '60px', border: '1px solid var(--border-medium)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px' }}>
                <Target size={28} color="var(--text-primary)" />
              </div>
              <h3 className="heading-3" style={{ marginBottom: '12px' }}>Authenticity</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                No gimmicks, no exaggerations. We share historically accurate narratives backed by research and local knowledge.
              </p>
            </div>
            <div>
              <div style={{ width: '60px', height: '60px', border: '1px solid var(--border-medium)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px' }}>
                <Users size={28} color="var(--text-primary)" />
              </div>
              <h3 className="heading-3" style={{ marginBottom: '12px' }}>Intimate Experiences</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Small groups ensure personal attention, meaningful interactions, and the space to ask questions and explore.
              </p>
            </div>
            <div>
              <div style={{ width: '60px', height: '60px', border: '1px solid var(--border-medium)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px' }}>
                <Award size={28} color="var(--text-primary)" />
              </div>
              <h3 className="heading-3" style={{ marginBottom: '12px' }}>Excellence</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                From our guides to our planning, we maintain the highest standards to deliver exceptional experiences every time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            <h2 className="heading-1" style={{ marginBottom: '24px' }}>Meet the Founder</h2>
            <div style={{ background: 'var(--bg-secondary)', padding: '48px 40px', border: '1px solid var(--border-light)' }}>
              <h3 className="heading-2" style={{ marginBottom: '12px' }}>Joshua Massey</h3>
              <p className="body-small" style={{ color: 'var(--text-light)', marginBottom: '24px' }}>Founder, Delhi Guided Tours</p>
              <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
                With a deep love for Delhi's history and architecture, Joshua founded Delhi Guided Tours to create experiences that go beyond surface-level tourism.
              </p>
              <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                His vision is simple: to share Delhi's stories with the world through authentic, expert-led tours that inspire curiosity and appreciation for India's incredible heritage.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ textAlign: 'center', marginBottom: '32px' }}>Our Commitment</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div style={{ padding: '24px', background: 'var(--bg-primary)', border: '1px solid var(--border-light)' }}>
                <h3 className="heading-3" style={{ marginBottom: '12px' }}>Safety First</h3>
                <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                  Your safety and comfort are our top priorities. All our vehicles are well-maintained, our guides are trained, and we follow best practices for group management.
                </p>
              </div>
              <div style={{ padding: '24px', background: 'var(--bg-primary)', border: '1px solid var(--border-light)' }}>
                <h3 className="heading-3" style={{ marginBottom: '12px' }}>Quality Assurance</h3>
                <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                  We continuously train our guides, refine our itineraries, and listen to guest feedback to ensure every tour meets our high standards.
                </p>
              </div>
              <div style={{ padding: '24px', background: 'var(--bg-primary)', border: '1px solid var(--border-light)' }}>
                <h3 className="heading-3" style={{ marginBottom: '12px' }}>Sustainable Tourism</h3>
                <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                  We believe in responsible tourism that respects heritage sites, local communities, and the environment. Small groups minimize our footprint while maximizing your experience.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)', textAlign: 'center' }}>
        <div className="container">
          <div style={{ maxWidth: '700px', margin: '0 auto' }}>
            <h2 className="hero-medium" style={{ marginBottom: '24px' }}>
              Join Us on a Journey Through Time
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '40px' }}>
              Experience Delhi's heritage the way it was meant to be discovered - through stories, insights, and authentic connections.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="/weekly-tours" className="btn-primary">View Our Tours</a>
              <a href="/contact" className="btn-primary">Get in Touch</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;